import React, { useState, useEffect } from 'react';
import { Home, Heart, Calendar, Mail, Menu as MenuIcon, X, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { BOOKING_URL } from '@/constants/urls';
import watwLogo from '../assets/watw_logo.webp';

interface MenuLink {
  label: string;
  href: string;
  external?: boolean;
}

interface MenuSection {
  title: string;
  links: MenuLink[];
}

const navItems = [
  { label: 'Home', icon: <Home />, href: '/' },
  { label: 'Services', icon: <Heart />, href: '/#services' },
  { label: 'Contact', icon: <Mail />, href: '/#contact' },
];

const menuSections: MenuSection[] = [
  { title: 'About Us', links: [
    { label: 'About', href: '/#about' },
    { label: 'Team', href: '/#team' },
    { label: 'Currimundi Clinic', href: '/currimundi-clinic' },
    { label: 'Online Consultations', href: '/online-consultations' },
  ]},
  { title: 'Life Stages', links: [
    { label: 'Teens', href: '/teens' },
    { label: 'Young Adults', href: '/young-adults' },
    { label: 'Preconception', href: '/preconception' },
    { label: 'Pregnancy', href: '/pregnancy' },
    { label: 'Birth Preparation', href: '/birth-prep' },
    { label: 'Postpartum', href: '/postpartum' },
    { label: 'Perimenopause', href: '/perimenopause' },
    { label: 'Menopause', href: '/menopause' },
    { label: 'Cycle-Free Health', href: '/cycle-free-health' },
  ]},
  { title: 'Support Services', links: [
    { label: 'Fertility Support', href: '/fertility-support' },
    { label: 'IVF Support', href: '/ivf-support' },
    { label: 'Emotional Healing', href: '/emotional-healing' },
    { label: 'Stress & Burnout', href: '/stress-burnout' },
    { label: 'Sleep Support', href: '/sleep-support' },
    { label: 'Digestive Health', href: '/digestion-gut-health' },
            { label: 'Cosmetic Acupuncture', href: '/cosmetic-acupuncture' },
    { label: 'Pain & Injury', href: '/pain-injury' },
    { label: 'Skin Health', href: '/skin-health' },
    { label: 'Immune Support', href: '/immune-support' },
    { label: 'Headache & Migraine', href: '/headache-migraine' },
    { label: 'Birth Preparation', href: '/birth-prep' },
    { label: 'Miscarriage Support', href: '/miscarriage-support' },
  ]},
  { title: 'Care Packages', links: [
    { label: 'Skin Health Package', href: '/care-packages/skin-package' },
    { label: 'Fertility Package', href: '/care-packages/fertility-package' },
  ]},
  { title: 'More', links: [
    { label: 'Blog', href: '/blog' },
    { label: 'Pricing', href: '/#pricing' },
    { label: 'FAQ', href: '/#faq' },
  ]},
];

const BottomNavBar: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  return (
    <>
      {/* Bottom NavBar */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-[60] bg-white border-t border-amber-200 shadow-2xl flex justify-between items-center px-2 py-1 lg:hidden bottom-nav rounded-t-5xl max-w-screen"
        role="navigation"
        style={{ zIndex: 60 }}
      >
        {navItems.slice(0, 2).map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="flex flex-col items-center flex-1 py-2 text-sage-700 hover:text-amber-600 focus:text-amber-700 focus:outline-none"
            aria-label={item.label}
          >
            {item.icon}
            <span className="text-xs mt-1 font-medium">{item.label}</span>
          </a>
        ))}

        {/* Book Now Floating Action Button */}
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="relative -top-6 bg-amber-600 hover:bg-amber-700 text-white rounded-full shadow-lg flex flex-col items-center justify-center w-16 h-16 border-4 border-white focus:outline-none focus:ring-2 focus:ring-amber-500"
          aria-label="Book Now"
        >
          <Calendar className="w-7 h-7 mb-1" />
          <span className="text-xs font-bold">Book</span>
        </a>

        {navItems.slice(2).map((item) => (
          <a
            key={item.label}
            href={item.href}
            className="flex flex-col items-center flex-1 py-2 text-sage-700 hover:text-amber-600 focus:text-amber-700 focus:outline-none"
            aria-label={item.label}
          >
            {item.icon}
            <span className="text-xs mt-1 font-medium">{item.label}</span>
          </a>
        ))}

        {/* Menu Button */}
        <button
          onClick={() => setMenuOpen(true)}
          className="flex flex-col items-center flex-1 py-2 text-sage-700 hover:text-amber-600 focus:text-amber-700 focus:outline-none"
          aria-label="Open Menu"
        >
          <MenuIcon className="w-6 h-6" />
          <span className="text-xs mt-1 font-medium">Menu</span>
        </button>
      </nav>

      {/* Menu Modal */}
      {menuOpen && (
        <div 
          className="fixed inset-0 z-[70] bg-black/20 flex items-end lg:hidden" 
          onClick={() => setMenuOpen(false)}
          style={{ zIndex: 70 }}
        >
          <div className="w-full bg-white rounded-t-3xl max-h-[90vh] flex flex-col shadow-2xl transform transition-transform duration-300 ease-out translate-y-0" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center px-6 pt-6 pb-4 flex-shrink-0 border-b border-amber-100">
              <span className="text-lg font-serif font-bold bg-gradient-to-r from-amber-500 to-amber-700 bg-clip-text text-transparent flex items-center gap-2">
                <img src={watwLogo} alt='Logo' className='w-7 h-7 mr-2' />
                Menu
              </span>
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 rounded-full hover:bg-amber-50 focus:outline-none focus:ring-2 focus:ring-amber-500"
                aria-label="Close Menu"
              >
                <X className="w-6 h-6 text-sage-700" />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0 px-6 pb-6 pt-2">
              {menuSections.map((section) => (
                <div key={section.title} className="mb-6">
                  <h3 className="text-sage-800 font-serif font-semibold mb-2 text-base">{section.title}</h3>
                  <ul className="space-y-2">
                    {section.links.map((link) => (
                      <li key={link.label}>
                        {link.external ? (
                          <a
                            href={link.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block px-3 py-2 rounded-lg text-sage-700 hover:bg-amber-50 hover:text-amber-700 transition-colors text-sm font-medium"
                            onClick={() => setMenuOpen(false)}
                            aria-label={link.label}
                          >
                            {link.label}
                          </a>
                        ) : (
                          <Link
                            to={link.href}
                            className="block px-3 py-2 rounded-lg text-sage-700 hover:bg-amber-50 hover:text-amber-700 transition-colors text-sm font-medium"
                            onClick={() => setMenuOpen(false)}
                            aria-label={link.label}
                          >
                            {link.label}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            
            {/* Menu Footer with Contact Info and Book Button */}
            <div className="px-6 pb-6 pt-4 border-t border-amber-100 space-y-4">
              <div className="space-y-3">
                <a href="tel:0415537784" className="flex items-center text-sage-700 hover:text-amber-600 p-3 rounded-lg hover:bg-amber-50 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50" aria-label="Call us at 0415 537 784">
                  <Phone className="h-5 w-5 mr-3" aria-hidden="true" />
                  <span className="text-sm font-medium">0415 537 784</span>
                </a>
                <a href="mailto:womenandtheway@gmail.com" className="flex items-center text-sage-700 hover:text-amber-600 p-3 rounded-lg hover:bg-amber-50 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-500/50" aria-label="Email us at womenandtheway@gmail.com">
                  <Mail className="h-5 w-5 mr-3" aria-hidden="true" />
                  <span className="text-sm font-medium">womenandtheway@gmail.com</span>
                </a>
              </div>
              <Button
                asChild
                className="w-full bg-amber-600 hover:bg-amber-700 text-white py-4 font-medium focus:ring-2 focus:ring-amber-500/50"
              >
                <a
                  href={BOOKING_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Book an appointment"
                  onClick={() => setMenuOpen(false)}
                >
                  Book Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar; 